/**
 * Supabase Auth Service
 *
 * Handles all authentication operations.
 * Replaces any existing auth mechanism with Supabase Auth.
 */

import { supabase } from './client';
import type { User, Session, AuthError } from '@supabase/supabase-js';

// Auth user type with profile
export interface AuthUser {
  id: string;
  email: string;
  name: string | null;
  avatar: string | null;
  createdAt: string;
}

// Auth state
export interface AuthState {
  user: AuthUser | null;
  session: Session | null;
  loading: boolean;
  error: string | null;
}

// Sign up data
export interface SignUpData {
  email: string;
  password: string;
  name?: string;
}

// Sign in data
export interface SignInData {
  email: string;
  password: string;
}

// Password reset data
export interface PasswordResetData {
  email: string;
}

// Update password data
export interface UpdatePasswordData {
  password: string;
}

// Update profile data
export interface UpdateProfileData {
  name?: string;
  avatar?: string;
}

// Map Supabase user to AuthUser
const mapUser = (user: User | null): AuthUser | null => {
  if (!user) return null;

  return {
    id: user.id,
    email: user.email || '',
    name: user.user_metadata?.name || user.user_metadata?.full_name || null,
    avatar: user.user_metadata?.avatar_url || user.user_metadata?.avatar || null,
    createdAt: user.created_at,
  };
};

class AuthService {
  /**
   * Get current user
   */
  async getCurrentUser(): Promise<AuthUser | null> {
    const { data: { user } } = await supabase.auth.getUser();
    return mapUser(user);
  }

  /**
   * Get current session
   */
  async getSession(): Promise<Session | null> {
    const { data: { session } } = await supabase.auth.getSession();
    return session;
  }

  /**
   * Sign up with email and password
   */
  async signUp(data: SignUpData): Promise<{ user: AuthUser | null; error: AuthError | null }> {
    const { data: result, error } = await supabase.auth.signUp({
      email: data.email,
      password: data.password,
      options: {
        data: {
          name: data.name,
        },
      },
    });

    if (error) {
      // console.error('Sign up failed:', error);
      return { user: null, error };
    }

    return { user: mapUser(result.user), error: null };
  }

  /**
   * Sign in with email and password
   */
  async signIn(data: SignInData): Promise<{ user: AuthUser | null; error: AuthError | null }> {
    const { data: result, error } = await supabase.auth.signInWithPassword({
      email: data.email,
      password: data.password,
    });

    if (error) {
      // console.error('Sign in failed:', error);
      return { user: null, error };
    }

    return { user: mapUser(result.user), error: null };
  }

  /**
   * Sign in with Google OAuth
   */
  async signInWithGoogle(): Promise<{ error: AuthError | null }> {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    if (error) {
      // console.error('Google sign in failed:', error);
    }

    return { error };
  }

  /**
   * Sign in with magic link (passwordless)
   */
  async signInWithMagicLink(email: string): Promise<{ error: AuthError | null }> {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    if (error) {
      // console.error('Magic link sign in failed:', error);
    }

    return { error };
  }

  /**
   * Sign out
   */
  async signOut(): Promise<{ error: AuthError | null }> {
    const { error } = await supabase.auth.signOut();

    if (error) {
      // console.error('Sign out failed:', error);
    }

    return { error };
  }

  /**
   * Send password reset email
   */
  async resetPassword(data: PasswordResetData): Promise<{ error: AuthError | null }> {
    const { error } = await supabase.auth.resetPasswordForEmail(data.email, {
      redirectTo: `${window.location.origin}/auth/reset-password`,
    });

    if (error) {
      // console.error('Password reset failed:', error);
    }

    return { error };
  }

  /**
   * Update password (after reset or while logged in)
   */
  async updatePassword(data: UpdatePasswordData): Promise<{ error: AuthError | null }> {
    const { error } = await supabase.auth.updateUser({
      password: data.password,
    });

    if (error) {
      // console.error('Password update failed:', error);
    }

    return { error };
  }

  /**
   * Update user profile
   */
  async updateProfile(data: UpdateProfileData): Promise<{ user: AuthUser | null; error: AuthError | null }> {
    const { data: result, error } = await supabase.auth.updateUser({
      data: {
        name: data.name,
        avatar_url: data.avatar,
      },
    });

    if (error) {
      // console.error('Profile update failed:', error);
      return { user: null, error };
    }

    return { user: mapUser(result.user), error: null };
  }

  /**
   * Refresh the session
   */
  async refreshSession(): Promise<{ session: Session | null; error: AuthError | null }> {
    const { data, error } = await supabase.auth.refreshSession();

    if (error) {
      // console.error('Session refresh failed:', error);
    }

    return { session: data.session, error };
  }

  /**
   * Listen to auth state changes
   */
  onAuthStateChange(callback: (user: AuthUser | null, session: Session | null) => void) {
    return supabase.auth.onAuthStateChange((event, session) => {
      // console.log('Auth state changed:', event);
      callback(mapUser(session?.user || null), session);
    });
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    const session = await this.getSession();
    return !!session;
  }

  /**
   * Get the access token for API calls
   */
  async getAccessToken(): Promise<string | null> {
    const session = await this.getSession();
    return session?.access_token || null;
  }

  /**
   * Verify OTP (for phone/email verification)
   */
  async verifyOtp(token: string, type: 'email' | 'sms', email?: string, phone?: string): Promise<{ error: AuthError | null }> {
    const verifyOptions = type === 'email'
      ? { token, type: 'email' as const, email: email || '' }
      : { token, type: 'sms' as const, phone: phone || '' };

    const { error } = await supabase.auth.verifyOtp(verifyOptions);

    if (error) {
      // console.error('OTP verification failed:', error);
    }

    return { error };
  }

  /**
   * Resend OTP/confirmation email
   */
  async resendConfirmation(email: string): Promise<{ error: AuthError | null }> {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
    });

    if (error) {
      // console.error('Resend confirmation failed:', error);
    }

    return { error };
  }
}

export const authService = new AuthService();

// Export type for auth state subscription
export type AuthSubscription = ReturnType<typeof authService.onAuthStateChange>;
